import type { MetadataResponse } from "@lumidb/lumidb";
import type * as GeoJSON from "geojson";
import { proxy } from "valtio";
import type { ExportFormat } from "./export";
import type { ColorMode } from "./point-material";
import type { ProjectTask } from "./project";
import type { RasterUpload, Upload } from "./upload";
import { clearArray } from "./utils";
import type { VectorImportFileSet } from "./vector-import";

export type TilesetMode = "tileset" | "i3s" | "ept";
export type ImportMode = keyof ReturnType<typeof initialImportInputs>;

export type ContextMenuItem = {
    icon?: React.ReactNode;
    label: string | React.ReactNode;
    onClick: (event: React.MouseEvent) => void | Promise<void>;
    disabled?: (() => boolean) | boolean;
    hidden?: () => boolean;
    className?: string;
    tooltip?: {
        label: string;
        message: string;
    };
};

export type ContextMenu = {
    position: { x: number; y: number };
    items: ContextMenuItem[];
};

export type Export = {
    id: string;
    status: "queued" | "processing" | "ready" | "failed";
    progress: number;
    filename?: string;
    format?: string;
    size: string;
    startedAt: string;
};

export type Outline = {
    boundary: GeoJSON.MultiPolygon;
    color: [number, number, number];
};

type CustomFieldValueType = "text" | "number" | "date";

export type CustomField =
    | { type: "preset"; key: string; label: string; valueType: CustomFieldValueType; value: string }
    | { type: "custom"; key: string; valueType: CustomFieldValueType; value: string };

export const COLOR_MODES: { label: string; value: ColorMode }[] = [
    { label: "RGB", value: "rgb" },
    { label: "Classification", value: "classification" },
    { label: "Intensity", value: "intensity" },
    { label: "Point Source ID", value: "sourceID" },
    { label: "File", value: "metaIndex" },
    { label: "Dataset", value: "dataset" },
    { label: "Elevation", value: "elevation" },
    { label: "Extra Data", value: "extra" },
    { label: "Object ID", value: "object" },
    { label: "Height Above Ground", value: "hag" },
];

export const POINT_CLASSES: { label: string; value: number }[] = [
    { label: "Never Classified", value: 0 },
    { label: "Unassigned", value: 1 },
    { label: "Ground", value: 2 },
    { label: "Low Vegetation", value: 3 },
    { label: "Medium Vegetation", value: 4 },
    { label: "High Vegetation", value: 5 },
    { label: "Building", value: 6 },
    { label: "Low Point (noise)", value: 7 },
    { label: "Reserved", value: 8 },
    { label: "Water", value: 9 },
    { label: "Rail", value: 10 },
    { label: "Road Surface", value: 11 },
    { label: "Reserved", value: 12 },
    { label: "Wire - Guard (Shield)", value: 13 },
    { label: "Wire - Conductor (Phase)", value: 14 },
    { label: "Transmission Tower", value: 15 },
    { label: "Wire-structure Connector", value: 16 },
    { label: "Bridge Deck", value: 17 },
    { label: "High Noise", value: 18 },
];

export type ToastPlacement = {
    right?: number;
    bottom?: number;
    left?: number;
    top?: number;
    initial: { x?: number; y?: number };
    exit?: { x?: number; y?: number };
};

export type Toast = {
    id: number;
    message: string;
    type: "info" | "error" | "success";
    placement?: ToastPlacement;
};

const initialImportInputs = () => ({
    vector: {
        fallbackCrs: "",
        fileSets: [] as VectorImportFileSet[],
        unmatchedFiles: [] as Upload[],
        duplicateFiles: [] as Upload[],
        error: null as string | null,
        importing: false,
    },
    pointcloud: {
        tableName: "",
        proj: "",
        projectionEdited: false,
        ellipsoidalZ: false,
        attribution: "",
        capturedAt: "",
        customFields: [] as CustomField[],
        uploads: [] as Upload[],
        error: null as string | null,
    },
    raster: {
        rasterName: "",
        tableProj: "",
        projectionEdited: false,
        attribution: "",
        capturedAt: "",
        customFields: [] as CustomField[],
        rasterUploads: [] as RasterUpload[],
        unmatchedFiles: [] as Upload[],
        error: null as string | null,
    },
    attachment: {
        groupName: "",
        groupNameEdited: false,
        description: "",
        customFields: [] as CustomField[],
        uploads: [] as Upload[],
        error: null as string | null,
    },
});

let toastId = 0;
export const nextToastId = () => ++toastId;

export class HUD {
    public state = proxy({
        toasts: [] as Toast[],
        contextMenus: [] as ContextMenu[],
        search: {
            open: false,
            query: "",
            results: [] as Array<{
                id: string;
                name: string;
                place_formatted?: string;
                center: [number, number];
                mapbox_id: string;
                feature_type: string;
                poi_category?: string[];
            }>,
            loading: false,
            selectedIndex: -1,
        },
        sidebar: {
            open: window.matchMedia("(min-width: 768px)").matches,
            collapsed: [] as string[],
        },
        importInput: {
            open: false,
            mode: "vector" as ImportMode,
            ...initialImportInputs(),
        },
        feedback: {
            open: false,
            topic: "" as string,
            message: "" as string,
            files: [] as File[],
            sending: false,
        },
        waitlist: {
            open: false,
        },
        compare: {
            tableFilter: "" as string,
            showFilters: false,
        },
        tasks: {
            open: false,
            right: 0,
            top: 0,
            items: null as ProjectTask[] | null,
        },
        pointCloudExport: {
            open: false,
            format: "Laz" as ExportFormat,
            estimatedPoints: 0,
        },
        originalFiles: {
            open: false,
            files: [] as Array<{
                original_filename: string;
                size_bytes: number;
                datasetName: string;
                sourceFileId: number;
                isRaster: boolean;
            }>,
        },
        tileset: {
            open: false,
            mode: "tileset" as TilesetMode,
            datasetId: null as string | null,
            toWebMercator: false,
        },
        moveDataset: {
            open: false,
            datasetId: null as string | null,
            kind: "dataset" as "dataset" | "raster" | "attachment",
            title: null as string | null,
            openOnMove: false,
        },
        rasterShare: {
            open: false,
            rasterId: null as string | null,
            mode: "xyz" as "xyz" | "wmts" | "arcgis" | "cog",
        },
        tour: {
            active: false,
            step: "gestures" as "gestures" | "sidebar" | "details" | "tools",
            gestures: { pan: false, zoom: false, rotate: false },
        },
        membersMenu: {
            open: false,
            right: 0,
            top: 0,
            input: "",
            role: "viewer" as "admin" | "editor" | "analyst" | "viewer",
        },
        datasets: {
            view: "list" as "list" | "map",
        },
        customBasemap: {
            open: false,
        },
    });

    resetImport(mode: ImportMode) {
        const input = this.state.importInput;
        if (input.mode === mode) input.open = false;
        Object.assign(input[mode], initialImportInputs()[mode]);
    }

    openImport(mode: ImportMode) {
        this.state.importInput.mode = mode;
        this.state.importInput.open = true;
    }

    openSceneLink(mode: TilesetMode, datasetId: string | null) {
        this.state.tileset.mode = mode;
        this.state.tileset.datasetId = datasetId;
        this.state.tileset.open = true;
    }

    toast(message: string, type: Toast["type"], placement?: ToastPlacement) {
        this.state.toasts.push({ id: nextToastId(), message, type, placement });
    }

    public showTasks() {
        const tasksButton = document.getElementById("tasks-button");
        if (tasksButton) {
            const rect = tasksButton.getBoundingClientRect();
            this.state.tasks.right = rect.right - 8;
            this.state.tasks.top = rect.bottom - 8;
            this.state.tasks.open = true;
        }
    }

    public reset() {
        clearArray(this.state.toasts);
        clearArray(this.state.contextMenus);
        this.state.search.open = false;
        this.state.search.query = "";
        clearArray(this.state.search.results);
        this.state.search.loading = false;
        this.state.search.selectedIndex = -1;
        clearArray(this.state.sidebar.collapsed);

        this.state.importInput.open = false;
        this.state.importInput.mode = "vector";
        this.resetImport("vector");
        this.resetImport("pointcloud");
        this.resetImport("raster");
        this.resetImport("attachment");

        this.state.feedback.open = false;
        this.state.feedback.topic = "";
        this.state.feedback.message = "";
        clearArray(this.state.feedback.files);
        this.state.feedback.sending = false;
        this.state.waitlist.open = false;
        this.state.compare.tableFilter = "";
        this.state.compare.showFilters = false;
        this.state.tasks.open = false;
        this.state.tasks.items = null;
        this.state.pointCloudExport.open = false;
        this.state.pointCloudExport.format = "Laz";
        this.state.pointCloudExport.estimatedPoints = 0;
        this.state.originalFiles.open = false;
        clearArray(this.state.originalFiles.files);
        this.state.tileset.open = false;
        this.state.tileset.mode = "tileset";
        this.state.tileset.datasetId = null;
        this.state.moveDataset.open = false;
        this.state.moveDataset.datasetId = null;
        this.state.moveDataset.kind = "dataset";
        this.state.moveDataset.title = null;
        this.state.moveDataset.openOnMove = false;
        this.state.rasterShare.open = false;
        this.state.rasterShare.rasterId = null;
        this.state.rasterShare.mode = "xyz";
        this.state.tour.active = false;
        this.state.tour.step = "gestures";
        this.state.tour.gestures = { pan: false, zoom: false, rotate: false };
        this.state.membersMenu.open = false;
        this.state.membersMenu.right = 0;
        this.state.membersMenu.top = 0;
        this.state.membersMenu.input = "";
        this.state.membersMenu.role = "viewer";
        this.state.customBasemap.open = false;
    }
}

type OriginalFiles = HUD["state"]["originalFiles"]["files"];

export function originalFileEntries(
    datasetName: string,
    fileMetadata: Record<string, { original_filename: string; size_bytes: number }>,
    isRaster: boolean,
): OriginalFiles {
    return Object.entries(fileMetadata)
        .map(([key, value]) => ({
            original_filename: value.original_filename,
            size_bytes: value.size_bytes,
            datasetName,
            sourceFileId: Number(key),
            isRaster,
        }))
        .sort((a, b) => a.original_filename.localeCompare(b.original_filename));
}

export function originalFilesFromMeta(meta: MetadataResponse): OriginalFiles {
    return Object.entries(meta.tables)
        .flatMap(([datasetName, datasetMeta]) =>
            originalFileEntries(datasetName, datasetMeta.file_metadata ?? {}, false),
        )
        .sort((a, b) => a.original_filename.localeCompare(b.original_filename));
}
